import {
  Partners,
  Policy,
  Quote,
  QUOTE_PERMISSIONS,
  QUOTE_STATUSES,
  QUOTE_WORKFLOW_STATUSES,
  QuoteLite,
  User,
} from '@lockton-apps/shared/interfaces';

import {
  DynamicTableBreakpoints,
  DynamicTableColumnTypes,
  DynamicTableContext,
  DynamicTableSortDirection,
  DynamicTableVariantsDef,
  MapPropArgs,
} from '../../interfaces/dynamic-table.interface';

import { getProposalUrl } from '@lockton-apps/shared/util/proposal-utils';
import { get, map, toLower } from 'lodash';
import { DynamicTableInlineSearchComponent } from '../../components/inline-search/dynamic-table-inline-search.component';
import { DynamicTableDateFormat } from '../../components/templates/date/date.component';
import { hasPermission } from '@lockton-apps/shared/util/helpers';
import {
  DEFAULT_CONTEXT_MENU_SIZE,
  DEFAULT_PAGINATION,
} from '../dynamic-table.constant';

type QuoteTableArgs = MapPropArgs<Quote, { brokers: User[] }>;

export const quoteActions: Record<
  string,
  DynamicTableContext<Quote, { brokers: User[] }>
> = {
  viewStatus: {
    id: 'viewStatus',
    label: $localize`View status`,
    type: 'link',
    icon: 'status',
    exposed: true,
    linkCreator: (entity: Quote) => ({
      path: `/enquiries/${entity.id}`,
    }),
  },
  assignToBroker: {
    id: 'assignToBroker',
    label: $localize`Assign to broker`,
    icon: 'broker',
    type: 'inlineComponent',
    exposed: true,
    inlineComponent: {
      id: 'assignToBroker',
      component: DynamicTableInlineSearchComponent,
      inputsCreator: ({ additionalData }) => ({
        placeholder: $localize`Search for a broker`,
        value: ({ entity }: QuoteTableArgs) => entity.assignedUserId,
        items: map(additionalData.brokers, (broker) => ({
          id: broker.userId,
          label: broker.displayName,
          searchString: toLower(broker.displayName),
          value: broker,
        })),
      }),
    },
  },
  editEnquiry: {
    id: 'editEnquiry',
    label: $localize`Edit enquiry`,
    type: 'link',
    icon: 'edit',
    linkCreator: (entity: Quote) => ({
      path: ['/enquiries', entity.id, 'edit'],
    }),
  },
  updateProposal: {
    id: 'updateProposal',
    label: $localize`Change proposal form`,
    type: 'action',
    icon: 'change',
    hidden: (quote: Quote) =>
      hasPermission(quote, QUOTE_PERMISSIONS.isQuoteReadonly),
  },
  viewProposal: {
    id: 'viewProposal',
    label: $localize`View proposal form`,
    type: 'link',
    icon: 'application_new',
    // disabled: (quote: Quote) => !quote.propFormId,
    linkCreator: (quote: Quote) => ({
      path: getProposalUrl(quote),
    }),
    hidden: (quote: Quote): boolean => quote.portalId === Partners.LSOS_ADDON,
  },
  assignContact: {
    id: 'assignContact',
    label: $localize`Assign contact`,
    type: 'action',
    icon: 'add_contact',
    exposed: true,
  },
  sendInvite: {
    id: 'sendInvite',
    label: $localize`Send renewal invite`,
    type: 'action',
    icon: 'application_sent',
    hidden: (quote: Quote) =>
      quote.status !== QUOTE_STATUSES.Initialized ||
      quote.portalId === Partners.ACCA,
    exposed: true,
  },
  viewClient: {
    id: 'viewClient',
    label: $localize`View client`,
    type: 'link',
    icon: 'client',
    linkCreator: (entity: Quote) => ({
      path: `/clients/${entity.clientId}`,
    }),
  },
  returnProposal: {
    id: 'returnProposal',
    label: $localize`Return proposal to client`,
    type: 'action',
    icon: 'application_released',
    hidden: (quote: Quote) => quote.status !== QUOTE_STATUSES.Initialized,
  },
  configureRenewal: {
    id: 'configureRenewal',
    label: $localize`Configure renewal`,
    type: 'link',
    icon: 'application_new',
    hidden: (quote: Quote) =>
      quote.workflowStatus !== QUOTE_WORKFLOW_STATUSES.NewProposal ||
      quote.portalId !== Partners.ACCA,
    linkCreator: (entity: Quote) => ({
      path: `/enquiries/${entity.id}/configuration`,
    }),
  },
};

export const policyActions: Record<string, DynamicTableContext<Policy>> = {
  viewPolicy: {
    id: 'viewPolicy',
    label: $localize`View policy`,
    type: 'link',
    icon: 'view_policy',
    exposed: true,
    linkCreator: (entity: Policy) => ({
      path: `/policies/${entity.id}`,
    }),
  },
  viewDocuments: {
    id: 'viewDocuments',
    label: $localize`View documents`,
    type: 'link',
    icon: 'view_doc',
    exposed: true,
    linkCreator: (entity: Policy) => ({
      path: `/clients/${entity.policyClients[0].id}/documents`,
    }),
  },
  viewClient: {
    id: 'viewClient',
    label: $localize`View client`,
    type: 'link',
    icon: 'client',
    linkCreator: (entity: Policy) => ({
      path: `/clients/${entity.policyClients[0].id}`,
    }),
  },
  uploadDocuments: {
    id: 'uploadDocuments',
    label: $localize`Upload documents`,
    type: 'link',
    icon: 'doc_upload',
    linkCreator: (entity: Policy) => ({
      path: ['/clients', entity.policyClients[0].id, 'documents'],
      queryParams: { uploadNew: true },
    }),
  },
  addContact: {
    id: 'addContact',
    label: $localize`Add a contact`,
    type: 'link',
    icon: 'add_contact',
    linkCreator: (entity: Policy) => ({
      path: ['/contacts', 'new'],
      queryParams: { client: entity.policyClients[0].id },
    }),
  },
  addPolicy: {
    id: 'addPolicy',
    label: $localize`Add policy`,
    type: 'link',
    icon: 'add_policy',
    linkCreator: (entity: Policy) => ({
      path: ['/policies', 'new'],
      queryParams: { client: entity.policyClients[0].id },
    }),
  },
};

export const QUOTES_BROKER_CONFIG_DEFAULT: DynamicTableVariantsDef = {
  columns: {
    title: {
      id: 'title',
      label: $localize`Client / product type`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'cob-title',
      props: {
        size: 48,
        path: 'classOfBusinessId',
        subtitlePath: 'clientName',
      },
      sortable: true,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: `0 0 calc(100% - ${DEFAULT_CONTEXT_MENU_SIZE})`,
          order: 1,
        },
        [DynamicTableBreakpoints.SM]: {
          flex: 4,
        },
      },
    },
    assignedBroker: {
      id: 'assignedBroker',
      label: $localize`Assigned broker`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'initials',
      props: {
        path: 'assignedUserName',
        size: 24,
      },
      sortable: true,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: 2,
          order: 3,
        },
        [DynamicTableBreakpoints.SM]: {
          order: 2,
        },
      },
    },
    assignedContacts: {
      id: 'assignedContacts',
      label: $localize`Assigned contacts`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'text',
      props: {
        path: 'assignedContactUsers',
        parsers: (value: { displayName: string }[]) => {
          if (!value.length) {
            return 'No assigned contacts';
          }
          return value.map(({ displayName }) => displayName).join(', ');
        },
      },
    },
    status: {
      id: 'status',
      label: $localize`Status`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'status',
      props: {
        path: 'status',
        entityType: 'quote',
        size: 24,
      },
      sortable: true,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: 1,
          order: 4,
        },
        [DynamicTableBreakpoints.SM]: {
          order: 3,
        },
      },
    },
    workflowStatus: {
      id: 'workflowStatus',
      label: $localize`Status`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'status',
      props: {
        path: 'workflowStatus',
        entityType: 'quoteV2',
        size: 24,
      },
      sortable: true,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: 1,
          order: 4,
        },
        [DynamicTableBreakpoints.SM]: {
          order: 3,
        },
      },
    },
    lastActivity: {
      id: 'lastActivity',
      label: $localize`Last action`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'date',
      props: {
        path: 'updatedDate',
        format: DynamicTableDateFormat.TO_NOW,
      },
      sortable: true,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: 2,
          order: 5,
        },
        [DynamicTableBreakpoints.SM]: {
          order: 4,
        },
      },
    },
    renewalDate: {
      id: 'renewalDate',
      label: $localize`Renewal date`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'date',
      props: {
        path: 'policyStartDate',
        format: DynamicTableDateFormat.DEFAULT,
      },
      sortable: true,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: 2,
          order: 5,
        },
        [DynamicTableBreakpoints.SM]: {
          order: 4,
        },
      },
    },
    additionalRequests: {
      id: 'additionalRequests',
      label: $localize`Additional requests`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'additional-requests',
      props: {
        path: 'addons',
      },
      sortable: true,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: 1,
          order: 3,
        },
      },
    },
    contextMenu: {
      id: 'contextMenu',
      label: undefined,
      type: DynamicTableColumnTypes.Context,
      props: {
        test: 'test',
        actions: [],
      },
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: `0 0 ${DEFAULT_CONTEXT_MENU_SIZE}`,
          order: 2,
        },
        [DynamicTableBreakpoints.SM]: {
          order: 10,
        },
      },
    },
  },
  sort: {
    active: 'updatedDate',
    direction: DynamicTableSortDirection.Desc,
  },
  pagination: DEFAULT_PAGINATION,
};

export const QUOTES_BROKER_CONFIG_POLICIES: DynamicTableVariantsDef = {
  columns: {
    title: {
      id: 'title',
      label: $localize`Policy type`,
      type: DynamicTableColumnTypes.Template,
      sortable: true,
      columnType: 'cob-title',
      props: {
        size: 48,
        path: 'classOfBusinessId',
        subtitlePath: 'policyClients[0].insuredName',
      },
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: `0 0 calc(100% - ${DEFAULT_CONTEXT_MENU_SIZE})`,
          order: 1,
        },
        [DynamicTableBreakpoints.SM]: {
          flex: 4,
        },
      },
    },
    startDate: {
      id: 'startDate',
      label: $localize`Start date`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'date',
      props: {
        path: 'inceptionDate',
      },
      sortable: true,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: 2,
          order: 3,
        },
        [DynamicTableBreakpoints.SM]: {
          order: 2,
        },
      },
    },
    expiryDate: {
      id: 'expiryDate',
      label: $localize`Expiry date`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'date',
      props: {
        path: 'expiryDate',
      },
      sortable: true,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: 2,
          order: 4,
        },
        [DynamicTableBreakpoints.SM]: {
          order: 3,
        },
      },
    },
    status: {
      id: 'status',
      label: $localize`Status`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'status',
      props: {
        path: 'status',
        entityType: 'policy',
        size: 24,
      },
      sortable: true,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: 1,
          order: 5,
        },
        [DynamicTableBreakpoints.SM]: {
          order: 4,
        },
      },
    },
    contextMenu: {
      id: 'contextMenu',
      label: undefined,
      type: DynamicTableColumnTypes.Context,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: `0 0 ${DEFAULT_CONTEXT_MENU_SIZE}`,
          order: 2,
        },
        [DynamicTableBreakpoints.SM]: {
          order: 5,
        },
      },
      props: {
        actions: [],
      },
    },
  },
  sort: {
    active: 'inceptionDate',
    direction: DynamicTableSortDirection.Desc,
  },
  pagination: DEFAULT_PAGINATION,
};

export const QUOTES_BROKER_CONFIG_CANCELLATIONS: DynamicTableVariantsDef = {
  columns: {
    title: {
      id: 'title',
      label: $localize`Client / product type`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'cob-title',
      props: {
        size: 48,
        path: 'policy.classOfBusinessId',
        subtitlePath: 'policy.policyClients[0].insuredName',
      },
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: '0 0 100%',
        },
        [DynamicTableBreakpoints.SM]: {
          flex: 2,
        },
      },
    },
    startDate: {
      id: 'startDate',
      label: $localize`Start date`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'date',
      props: {
        path: 'policy.inceptionDate',
      },
      sortable: true,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: 1,
        },
      },
    },
    expiryDate: {
      id: 'expiryDate',
      label: $localize`Expiry date`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'date',
      props: {
        path: 'policy.expiryDate',
      },
      sortable: true,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: 1,
        },
      },
    },

    requestedCancellationDate: {
      id: 'requestedCancellationDate',
      label: $localize`Requested cancellation date`,
      type: DynamicTableColumnTypes.Template,
      columnType: 'date',
      props: {
        path: 'requestedDate',
      },
      sortable: true,
      layout: {
        [DynamicTableBreakpoints.DEFAULT]: {
          flex: 1,
        },
      },
    },
  },
  sort: {
    active: 'requestedDate',
    direction: DynamicTableSortDirection.Desc,
  },
  pagination: DEFAULT_PAGINATION,
};
